<template>
  <div class="fusion-payment-page">
    <div class="page-heading">
      <div>
        <h1>融合支付配置</h1>
        <p>先匹配银行专用规则；未命中时，再按公司主体和商户权重进行兜底分配。</p>
        <a-typography-text type="secondary">
          今日新注册用户首付统计更新时间：{{ statsUpdatedAt || "暂无统计数据" }}
        </a-typography-text>
      </div>
      <a-space wrap>
        <a-button
          class="primary-action-button"
          :loading="loading"
          :disabled="!!savingScope"
          @click="loadConfig"
        >
          <template #icon><ReloadOutlined /></template>
          刷新配置
        </a-button>
      </a-space>
    </div>

    <a-alert type="info" show-icon class="rule-description">
      <template #message>路由规则</template>
      <template #description>
        <ol>
          <li>请求银行命中“按银行路由”时，只在该规则启用的商户中按“今日新注册用户首付金额 ÷ 权重”选择。</li>
          <li>银行未命中专用规则时，进入“按公司兜底”：先选择公司，再在公司内过滤支持该银行的商户。</li>
          <li>权重必须为正整数；权重越大，目标承接金额越高。关闭的规则或商户不会参与路由。</li>
          <li>统计仅包含注册日期和支付日期均为当天、支付次数为 1 的快钱/合利宝成功付款，最多延迟约 5 分钟。</li>
        </ol>
      </template>
    </a-alert>

    <a-card v-if="loading" :bordered="false">
      <a-skeleton active :paragraph="{ rows: 10 }" />
    </a-card>

    <a-result
      v-else-if="loadError"
      status="error"
      title="融合支付配置加载失败"
      :sub-title="loadError"
      class="load-result"
    >
      <template #extra>
        <a-button type="primary" @click="loadConfig">重新加载</a-button>
      </template>
    </a-result>

    <template v-else>
      <a-alert
        v-if="!options.merchants.length || !options.banks.length"
        type="warning"
        show-icon
        class="option-warning"
        message="可选数据不完整"
        description="当前没有可用商户或银行，请先检查后端基础配置后再保存。"
      />

      <a-card :bordered="false" class="config-section">
        <template #title>
          <div class="section-title">
            <BankOutlined />
            <span>按银行路由</span>
            <a-tag color="blue">优先匹配</a-tag>
          </div>
        </template>
        <template #extra>
          <a-space wrap class="section-actions">
            <a-button
              class="primary-action-button"
              :loading="savingScope === 'bank'"
              :disabled="loading || !!savingScope"
              @click="handleSave('bank')"
            >
              <template #icon><SaveOutlined /></template>
              保存银行规则
            </a-button>
            <a-button
              class="add-rule-button"
              :disabled="loading || !!savingScope"
              @click="addBankRule"
            >
              <template #icon><PlusOutlined /></template>
              添加银行规则
            </a-button>
          </a-space>
        </template>

        <a-empty v-if="bankRules.length === 0" description="尚未配置银行专用路由">
          <a-button class="add-rule-button" :disabled="!!savingScope" @click="addBankRule">
            添加第一条银行规则
          </a-button>
        </a-empty>

        <div v-else class="rule-list">
          <a-card
            v-for="(rule, ruleIndex) in bankRules"
            :key="rule._key"
            size="small"
            class="rule-card"
            :class="{ 'is-disabled': !rule.isEnabled }"
          >
            <template #title>
              <a-space wrap>
                <span>银行规则 {{ ruleIndex + 1 }}</span>
                <a-tag :color="rule.isEnabled ? 'green' : 'default'">
                  {{ rule.isEnabled ? "启用" : "停用" }}
                </a-tag>
              </a-space>
            </template>
            <template #extra>
              <a-space wrap>
                <a-typography-text type="secondary" class="amount-text">
                  今日新客首付合计 {{ formatAmount(sumPayAmount(rule.merchants)) }}
                </a-typography-text>
                <a-switch
                  v-model:checked="rule.isEnabled"
                  checked-children="启用"
                  un-checked-children="停用"
                />
                <a-popconfirm
                  title="确定删除这条银行规则吗？"
                  ok-text="删除"
                  cancel-text="取消"
                  @confirm="removeBankRule(ruleIndex)"
                >
                  <a-button type="text" danger :aria-label="`删除银行规则 ${ruleIndex + 1}`">
                    <template #icon><DeleteOutlined /></template>
                  </a-button>
                </a-popconfirm>
              </a-space>
            </template>

            <a-form layout="vertical">
              <a-row :gutter="16">
                <a-col :xs="24" :lg="10">
                  <a-form-item
                    label="银行"
                    required
                    :validate-status="fieldStatus(rule._key, 'bankCode')"
                    :help="fieldError(rule._key, 'bankCode')"
                  >
                    <a-select
                      v-model:value="rule.bankCode"
                      :options="bankRuleOptions(rule)"
                      show-search
                      allow-clear
                      :filter-option="filterSelectOption"
                      placeholder="请选择银行"
                      @change="clearFieldError(rule._key, 'bankCode')"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :lg="14">
                  <a-form-item label="规则备注">
                    <a-input
                      v-model:value="rule.remark"
                      allow-clear
                      :maxlength="255"
                      placeholder="选填，例如：招商银行专用商户"
                    />
                  </a-form-item>
                </a-col>
              </a-row>
            </a-form>

            <div class="merchant-list-heading">
              <div>
                <strong>候选商户</strong>
                <a-typography-text type="secondary">命中该银行后直接在以下商户中均衡</a-typography-text>
              </div>
              <a-button size="small" @click="addBankMerchant(rule)">
                <template #icon><PlusOutlined /></template>
                添加商户
              </a-button>
            </div>
            <a-alert
              v-if="fieldError(rule._key, 'merchants')"
              type="error"
              show-icon
              class="inline-error"
              :message="fieldError(rule._key, 'merchants')"
            />

            <a-empty
              v-if="rule.merchants.length === 0"
              :image="simpleImage"
              description="请至少添加一个候选商户"
              class="nested-empty"
            />
            <div v-else class="merchant-list">
              <div
                v-for="(merchant, merchantIndex) in rule.merchants"
                :key="merchant._key"
                class="merchant-editor"
                :class="{ 'is-disabled': !merchant.isEnabled }"
              >
                <div class="merchant-editor-heading">
                  <a-space wrap>
                    <strong>商户 {{ merchantIndex + 1 }}</strong>
                    <a-tag>{{ merchantChannelLabel(merchant.merchantId) }}</a-tag>
                    <a-typography-text type="secondary" class="amount-text">
                      今日新客首付 {{ formatAmount(merchant.todayPayAmount) }}
                    </a-typography-text>
                  </a-space>
                  <a-popconfirm
                    title="确定移除这个商户吗？"
                    ok-text="移除"
                    cancel-text="取消"
                    @confirm="removeMerchant(rule, merchantIndex)"
                  >
                    <a-button type="text" danger :aria-label="`移除银行规则商户 ${merchantIndex + 1}`">
                      <template #icon><DeleteOutlined /></template>
                    </a-button>
                  </a-popconfirm>
                </div>
                <a-form layout="vertical">
                  <a-row :gutter="12">
                    <a-col :xs="24" :md="10">
                      <a-form-item
                        label="商户"
                        required
                        :validate-status="fieldStatus(merchant._key, 'merchantId')"
                        :help="fieldError(merchant._key, 'merchantId')"
                      >
                        <a-select
                          v-model:value="merchant.merchantId"
                          :options="bankMerchantOptions(rule, merchant)"
                          show-search
                          allow-clear
                          :filter-option="filterSelectOption"
                          placeholder="请选择商户"
                          @change="clearFieldError(merchant._key, 'merchantId')"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :xs="12" :md="4">
                      <a-form-item
                        label="权重"
                        required
                        :validate-status="fieldStatus(merchant._key, 'weight')"
                        :help="fieldError(merchant._key, 'weight')"
                      >
                        <a-input-number
                          v-model:value="merchant.weight"
                          :min="1"
                          :precision="0"
                          style="width: 100%"
                          @change="clearFieldError(merchant._key, 'weight')"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :xs="12" :md="4">
                      <a-form-item label="状态">
                        <a-switch
                          v-model:checked="merchant.isEnabled"
                          checked-children="启用"
                          un-checked-children="停用"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :xs="24" :md="6">
                      <a-form-item label="商户备注">
                        <a-input
                          v-model:value="merchant.remark"
                          allow-clear
                          :maxlength="255"
                          placeholder="选填"
                        />
                      </a-form-item>
                    </a-col>
                  </a-row>
                </a-form>
              </div>
            </div>
          </a-card>
        </div>
      </a-card>

      <a-card :bordered="false" class="config-section">
        <template #title>
          <div class="section-title">
            <ApartmentOutlined />
            <span>按公司兜底</span>
            <a-tag color="orange">银行未命中时使用</a-tag>
          </div>
        </template>
        <template #extra>
          <a-space wrap class="section-actions">
            <a-button
              class="primary-action-button"
              :loading="savingScope === 'company'"
              :disabled="loading || !!savingScope"
              @click="handleSave('company')"
            >
              <template #icon><SaveOutlined /></template>
              保存公司规则
            </a-button>
            <a-button
              class="add-rule-button"
              :disabled="loading || !!savingScope"
              @click="addCompanyRule"
            >
              <template #icon><PlusOutlined /></template>
              添加公司
            </a-button>
          </a-space>
        </template>

        <a-empty v-if="companyRules.length === 0" description="尚未配置公司兜底路由">
          <a-button class="add-rule-button" :disabled="!!savingScope" @click="addCompanyRule">
            添加第一个公司
          </a-button>
        </a-empty>

        <div v-else class="rule-list">
          <a-card
            v-for="(rule, ruleIndex) in companyRules"
            :key="rule._key"
            size="small"
            class="rule-card company-rule-card"
            :class="{ 'is-disabled': !rule.isEnabled }"
          >
            <template #title>
              <a-space wrap>
                <span>公司规则 {{ ruleIndex + 1 }}</span>
                <a-tag :color="rule.isEnabled ? 'green' : 'default'">
                  {{ rule.isEnabled ? "启用" : "停用" }}
                </a-tag>
              </a-space>
            </template>
            <template #extra>
              <a-space wrap>
                <a-typography-text type="secondary" class="amount-text">
                  今日新客首付合计 {{ formatAmount(rule.todayPayAmount ?? sumPayAmount(rule.merchants)) }}
                </a-typography-text>
                <a-switch
                  v-model:checked="rule.isEnabled"
                  checked-children="启用"
                  un-checked-children="停用"
                />
                <a-popconfirm
                  title="确定删除这条公司规则吗？"
                  ok-text="删除"
                  cancel-text="取消"
                  @confirm="removeCompanyRule(ruleIndex)"
                >
                  <a-button type="text" danger :aria-label="`删除公司规则 ${ruleIndex + 1}`">
                    <template #icon><DeleteOutlined /></template>
                  </a-button>
                </a-popconfirm>
              </a-space>
            </template>

            <a-form layout="vertical">
              <a-row :gutter="16">
                <a-col :xs="24" :lg="10">
                  <a-form-item
                    label="公司主体"
                    required
                    :validate-status="fieldStatus(rule._key, 'companyName')"
                    :help="fieldError(rule._key, 'companyName')"
                  >
                    <a-select
                      v-model:value="rule.companyName"
                      :options="companyRuleOptions(rule)"
                      show-search
                      allow-clear
                      :filter-option="filterSelectOption"
                      placeholder="请选择公司"
                      @change="clearFieldError(rule._key, 'companyName')"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="12" :lg="4">
                  <a-form-item
                    label="公司权重"
                    required
                    :validate-status="fieldStatus(rule._key, 'weight')"
                    :help="fieldError(rule._key, 'weight')"
                  >
                    <a-input-number
                      v-model:value="rule.weight"
                      :min="1"
                      :precision="0"
                      style="width: 100%"
                      @change="clearFieldError(rule._key, 'weight')"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :lg="10">
                  <a-form-item label="规则备注">
                    <a-input
                      v-model:value="rule.remark"
                      allow-clear
                      :maxlength="255"
                      placeholder="选填，例如：默认收款主体"
                    />
                  </a-form-item>
                </a-col>
              </a-row>
            </a-form>

            <div class="merchant-list-heading">
              <div>
                <strong>公司商户</strong>
                <a-typography-text type="secondary">商户仅在配置的银行范围内参与兜底</a-typography-text>
              </div>
              <a-button size="small" :disabled="!rule.companyName" @click="addCompanyMerchant(rule)">
                <template #icon><PlusOutlined /></template>
                添加商户
              </a-button>
            </div>
            <a-alert
              v-if="fieldError(rule._key, 'merchants')"
              type="error"
              show-icon
              class="inline-error"
              :message="fieldError(rule._key, 'merchants')"
            />

            <a-empty
              v-if="rule.merchants.length === 0"
              :image="simpleImage"
              description="选择公司后，请至少添加一个商户"
              class="nested-empty"
            />
            <div v-else class="merchant-list">
              <div
                v-for="(merchant, merchantIndex) in rule.merchants"
                :key="merchant._key"
                class="merchant-editor"
                :class="{ 'is-disabled': !merchant.isEnabled }"
              >
                <div class="merchant-editor-heading">
                  <a-space wrap>
                    <strong>商户 {{ merchantIndex + 1 }}</strong>
                    <a-tag>{{ merchantChannelLabel(merchant.merchantId) }}</a-tag>
                    <a-typography-text type="secondary" class="amount-text">
                      今日新客首付 {{ formatAmount(merchant.todayPayAmount) }}
                    </a-typography-text>
                  </a-space>
                  <a-popconfirm
                    title="确定移除这个商户吗？"
                    ok-text="移除"
                    cancel-text="取消"
                    @confirm="removeMerchant(rule, merchantIndex)"
                  >
                    <a-button type="text" danger :aria-label="`移除公司规则商户 ${merchantIndex + 1}`">
                      <template #icon><DeleteOutlined /></template>
                    </a-button>
                  </a-popconfirm>
                </div>
                <a-form layout="vertical">
                  <a-row :gutter="12">
                    <a-col :xs="24" :md="10">
                      <a-form-item
                        label="商户"
                        required
                        :validate-status="fieldStatus(merchant._key, 'merchantId')"
                        :help="fieldError(merchant._key, 'merchantId')"
                      >
                        <a-select
                          v-model:value="merchant.merchantId"
                          :options="companyMerchantOptions(rule, merchant)"
                          show-search
                          allow-clear
                          :filter-option="filterSelectOption"
                          placeholder="请选择该公司下的商户"
                          @change="handleCompanyMerchantChange(merchant)"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :xs="12" :md="4">
                      <a-form-item
                        label="权重"
                        required
                        :validate-status="fieldStatus(merchant._key, 'weight')"
                        :help="fieldError(merchant._key, 'weight')"
                      >
                        <a-input-number
                          v-model:value="merchant.weight"
                          :min="1"
                          :precision="0"
                          style="width: 100%"
                          @change="clearFieldError(merchant._key, 'weight')"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :xs="12" :md="4">
                      <a-form-item label="状态">
                        <a-switch
                          v-model:checked="merchant.isEnabled"
                          checked-children="启用"
                          un-checked-children="停用"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :xs="24" :md="6">
                      <a-form-item label="商户备注">
                        <a-input
                          v-model:value="merchant.remark"
                          allow-clear
                          :maxlength="255"
                          placeholder="选填"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :span="24">
                      <a-form-item
                        label="支持银行"
                        required
                        :validate-status="fieldStatus(merchant._key, 'bankCodes')"
                        :help="fieldError(merchant._key, 'bankCodes')"
                      >
                        <a-select
                          v-model:value="merchant.bankCodes"
                          mode="multiple"
                          :options="merchantBankOptions(merchant)"
                          show-search
                          allow-clear
                          :filter-option="filterSelectOption"
                          placeholder="请选择该商户可收款的银行"
                          @change="clearFieldError(merchant._key, 'bankCodes')"
                        />
                      </a-form-item>
                    </a-col>
                  </a-row>
                </a-form>
              </div>
            </div>
          </a-card>
        </div>
      </a-card>

      <div class="bottom-save-area">
        <a-button
          class="primary-action-button bottom-save-button"
          :loading="savingScope === 'all'"
          :disabled="loading || !!savingScope"
          @click="handleSave('all')"
        >
          <template #icon><SaveOutlined /></template>
          保存全部配置
        </a-button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import { Empty, message } from "ant-design-vue";
import {
  ApartmentOutlined,
  BankOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons-vue";
import { analyticsUrl } from "@/utils/api";
import { get, postJson } from "@/utils/request";

const API_PATH = "/operate/fusion-payment";
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;

const loading = ref(false);
const savingScope = ref("");
const loadError = ref("");
const statsUpdatedAt = ref("");
const bankRules = ref([]);
const companyRules = ref([]);
const validationErrors = ref({});
const options = reactive({
  merchants: [],
  companies: [],
  banks: [],
});

let keySequence = 0;
const createKey = (prefix) => `${prefix}-${Date.now()}-${++keySequence}`;
const toArray = (value) => (Array.isArray(value) ? value : []);
const toId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) ? id : undefined;
};
const normalizeChannel = (value) => {
  const channel = String(value || "").trim().toLowerCase();
  if (["kq", "kuaiqian"].includes(channel) || channel.includes("快钱")) return "kq";
  if (["hlb", "helibao"].includes(channel) || channel.includes("合利宝")) return "hlb";
  return channel;
};
const channelLabel = (channel) => {
  if (normalizeChannel(channel) === "kq") return "快钱";
  if (normalizeChannel(channel) === "hlb") return "合利宝";
  return channel || "未知渠道";
};

const makeMerchantRule = (source = {}, withBanks = false) => ({
  _key: createKey("merchant"),
  id: source.id ?? null,
  merchantId: toId(source.merchantId),
  weight: Number(source.weight ?? 1),
  isEnabled: source.isEnabled !== false,
  remark: source.remark || "",
  todayPayAmount: Number(source.todayPayAmount || 0),
  ...(withBanks ? { bankCodes: toArray(source.bankCodes).map(String) } : {}),
});

const makeBankRule = (source = {}) => ({
  _key: createKey("bank"),
  id: source.id ?? null,
  bankCode: source.bankCode ? String(source.bankCode) : undefined,
  bankName: source.bankName || "",
  isEnabled: source.isEnabled !== false,
  remark: source.remark || "",
  merchants: toArray(source.merchants).map((item) => makeMerchantRule(item)),
});

const makeCompanyRule = (source = {}) => ({
  _key: createKey("company"),
  id: source.id ?? null,
  companyName: source.companyName || undefined,
  weight: Number(source.weight ?? 1),
  isEnabled: source.isEnabled !== false,
  remark: source.remark || "",
  todayPayAmount: Number(source.todayPayAmount || 0),
  merchants: toArray(source.merchants).map((item) => makeMerchantRule(item, true)),
});

const applyOptions = (data = {}) => {
  options.merchants = toArray(data.merchants)
    .map((item) => ({
      id: toId(item.id),
      name: item.name || `商户 ${item.id}`,
      companyName: item.companyName || "",
      channel: normalizeChannel(item.channel),
    }))
    .filter((item) => item.id != null);

  options.companies = toArray(data.companies)
    .map((item) => ({
      name: item.name || "",
      merchantIds: toArray(item.merchantIds).map(toId).filter((id) => id != null),
    }))
    .filter((item) => item.name);

  const bankMap = new Map();
  const mergeBank = (item, fallbackChannel) => {
    if (!item?.code) return;
    const code = String(item.code);
    const current = bankMap.get(code) || {
      code,
      name: item.name || code,
      channels: [],
    };
    const channels = toArray(item.channels).map(normalizeChannel).filter(Boolean);
    if (fallbackChannel) channels.push(fallbackChannel);
    current.channels = [...new Set([...current.channels, ...channels])];
    if (item.name) current.name = item.name;
    bankMap.set(code, current);
  };
  toArray(data.banks).forEach((item) => mergeBank(item));
  toArray(data.kqBanks).forEach((item) => mergeBank(item, "kq"));
  toArray(data.hlbBanks).forEach((item) => mergeBank(item, "hlb"));
  options.banks = [...bankMap.values()];
};

const applyDetail = (data = {}) => {
  bankRules.value = toArray(data.bankRules).map(makeBankRule);
  companyRules.value = toArray(data.companyRules).map(makeCompanyRule);
  statsUpdatedAt.value = data.statsUpdatedAt || "";
  validationErrors.value = {};
};

const merchantMap = computed(
  () => new Map(options.merchants.map((merchant) => [merchant.id, merchant]))
);
const bankMap = computed(() => new Map(options.banks.map((bank) => [bank.code, bank])));
const companyMap = computed(
  () => new Map(options.companies.map((company) => [company.name, company]))
);

const merchantLabel = (merchant) =>
  `${merchant.name}（ID ${merchant.id} / ${channelLabel(merchant.channel)}）`;
const bankLabel = (bank) => {
  const channelText = bank.channels.length
    ? ` / ${bank.channels.map(channelLabel).join("、")}`
    : "";
  return `${bank.name}（${bank.code}${channelText}）`;
};
const filterSelectOption = (input, option) =>
  String(option?.label || "").toLowerCase().includes(String(input || "").toLowerCase());

const bankSupportsMerchant = (bankCode, merchantId) => {
  const bank = bankMap.value.get(bankCode);
  const merchant = merchantMap.value.get(merchantId);
  if (!bank || !merchant) return false;
  return !bank.channels.length || bank.channels.includes(normalizeChannel(merchant.channel));
};

const bankRuleOptions = (currentRule) => {
  const selectedCodes = new Set(
    bankRules.value
      .filter((rule) => rule._key !== currentRule._key && rule.bankCode)
      .map((rule) => rule.bankCode)
  );
  return options.banks.map((bank) => ({
    value: bank.code,
    label: bankLabel(bank),
    disabled: selectedCodes.has(bank.code),
  }));
};

const bankMerchantOptions = (rule, currentMerchant) => {
  const selectedIds = new Set(
    rule.merchants
      .filter((item) => item._key !== currentMerchant._key && item.merchantId != null)
      .map((item) => item.merchantId)
  );
  return options.merchants
    .filter(
      (merchant) =>
        !rule.bankCode ||
        bankSupportsMerchant(rule.bankCode, merchant.id) ||
        merchant.id === currentMerchant.merchantId
    )
    .map((merchant) => ({
      value: merchant.id,
      label: `${merchantLabel(merchant)} / ${merchant.companyName || "未设置公司"}`,
      disabled: selectedIds.has(merchant.id),
    }));
};

const companyRuleOptions = (currentRule) => {
  const selectedNames = new Set(
    companyRules.value
      .filter((rule) => rule._key !== currentRule._key && rule.companyName)
      .map((rule) => rule.companyName)
  );
  return options.companies.map((company) => ({
    value: company.name,
    label: `${company.name}（${company.merchantIds.length} 个商户）`,
    disabled: selectedNames.has(company.name),
  }));
};

const companyMerchantIds = (companyName) => {
  const company = companyMap.value.get(companyName);
  if (company?.merchantIds?.length) return new Set(company.merchantIds);
  return new Set(
    options.merchants
      .filter((merchant) => merchant.companyName === companyName)
      .map((merchant) => merchant.id)
  );
};

const companyMerchantOptions = (rule, currentMerchant) => {
  const allowedIds = companyMerchantIds(rule.companyName);
  const selectedIds = new Set(
    rule.merchants
      .filter((item) => item._key !== currentMerchant._key && item.merchantId != null)
      .map((item) => item.merchantId)
  );
  return options.merchants
    .filter(
      (merchant) => allowedIds.has(merchant.id) || merchant.id === currentMerchant.merchantId
    )
    .map((merchant) => ({
      value: merchant.id,
      label: merchantLabel(merchant),
      disabled: selectedIds.has(merchant.id),
    }));
};

const merchantBankOptions = (merchantRule) => {
  const merchant = merchantMap.value.get(merchantRule.merchantId);
  return options.banks
    .filter(
      (bank) =>
        !merchant ||
        !bank.channels.length ||
        bank.channels.includes(normalizeChannel(merchant.channel)) ||
        merchantRule.bankCodes.includes(bank.code)
    )
    .map((bank) => ({ value: bank.code, label: bankLabel(bank) }));
};

const merchantChannelLabel = (merchantId) => {
  const merchant = merchantMap.value.get(merchantId);
  return merchant ? channelLabel(merchant.channel) : "待选择";
};

const sumPayAmount = (merchants) =>
  toArray(merchants).reduce((sum, merchant) => sum + Number(merchant.todayPayAmount || 0), 0);
const formatAmount = (amount) => {
  const cents = Number(amount || 0);
  return `¥${(cents / 100).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const addBankRule = () => {
  const rule = makeBankRule();
  rule.merchants.push(makeMerchantRule());
  bankRules.value.push(rule);
};
const addCompanyRule = () => {
  const rule = makeCompanyRule();
  rule.merchants.push(makeMerchantRule({}, true));
  companyRules.value.push(rule);
};
const addBankMerchant = (rule) => {
  rule.merchants.push(makeMerchantRule());
  clearFieldError(rule._key, "merchants");
};
const addCompanyMerchant = (rule) => {
  rule.merchants.push(makeMerchantRule({}, true));
  clearFieldError(rule._key, "merchants");
};
const removeBankRule = (index) => bankRules.value.splice(index, 1);
const removeCompanyRule = (index) => companyRules.value.splice(index, 1);
const removeMerchant = (rule, index) => rule.merchants.splice(index, 1);
const handleCompanyMerchantChange = (merchant) => {
  merchant.bankCodes = [];
  clearFieldError(merchant._key, "merchantId");
  clearFieldError(merchant._key, "bankCodes");
};

const fieldKey = (ownerKey, field) => `${ownerKey}.${field}`;
const fieldError = (ownerKey, field) => validationErrors.value[fieldKey(ownerKey, field)] || "";
const fieldStatus = (ownerKey, field) => (fieldError(ownerKey, field) ? "error" : undefined);
const clearFieldError = (ownerKey, field) => {
  const key = fieldKey(ownerKey, field);
  if (!validationErrors.value[key]) return;
  const nextErrors = { ...validationErrors.value };
  delete nextErrors[key];
  validationErrors.value = nextErrors;
};
const isPositiveInteger = (value) => Number.isInteger(Number(value)) && Number(value) > 0;
const normalizeSaveScope = (scope) =>
  ["bank", "company"].includes(scope) ? scope : "all";

const validateConfig = (scope = "all") => {
  const targetScope = normalizeSaveScope(scope);
  const errors = {};
  const setError = (ownerKey, field, content) => {
    errors[fieldKey(ownerKey, field)] = content;
  };

  if (
    targetScope === "all" &&
    !bankRules.value.length &&
    !companyRules.value.length
  ) {
    validationErrors.value = {};
    message.warning("至少配置一条银行规则或公司规则；如需暂停路由，请停用现有规则");
    return false;
  }

  if (targetScope !== "company") {
    const seenBankCodes = new Map();
    bankRules.value.forEach((rule) => {
      if (!rule.bankCode || !bankMap.value.has(rule.bankCode)) {
        setError(rule._key, "bankCode", "请选择有效银行");
      } else if (seenBankCodes.has(rule.bankCode)) {
        setError(rule._key, "bankCode", "同一银行只能配置一条规则");
        setError(
          seenBankCodes.get(rule.bankCode),
          "bankCode",
          "同一银行只能配置一条规则"
        );
      } else {
        seenBankCodes.set(rule.bankCode, rule._key);
      }

      if (!rule.merchants.length) {
        setError(rule._key, "merchants", "请至少添加一个候选商户");
      } else if (
        rule.isEnabled &&
        !rule.merchants.some((merchant) => merchant.isEnabled)
      ) {
        setError(rule._key, "merchants", "启用的银行规则至少需要一个启用商户");
      }
      const seenMerchants = new Map();
      rule.merchants.forEach((merchant) => {
        const target = merchantMap.value.get(merchant.merchantId);
        if (!target) {
          setError(merchant._key, "merchantId", "请选择有效商户");
        } else if (seenMerchants.has(merchant.merchantId)) {
          setError(merchant._key, "merchantId", "同一规则内不能重复选择商户");
          setError(
            seenMerchants.get(merchant.merchantId),
            "merchantId",
            "同一规则内不能重复选择商户"
          );
        } else if (
          rule.bankCode &&
          !bankSupportsMerchant(rule.bankCode, merchant.merchantId)
        ) {
          setError(merchant._key, "merchantId", "该商户渠道不支持当前银行");
        } else {
          seenMerchants.set(merchant.merchantId, merchant._key);
        }
        if (!isPositiveInteger(merchant.weight)) {
          setError(merchant._key, "weight", "权重必须为正整数");
        }
      });
    });
  }

  if (targetScope !== "bank") {
    const seenCompanies = new Map();
    companyRules.value.forEach((rule) => {
      if (!rule.companyName || !companyMap.value.has(rule.companyName)) {
        setError(rule._key, "companyName", "请选择有效公司主体");
      } else if (seenCompanies.has(rule.companyName)) {
        setError(rule._key, "companyName", "同一公司只能配置一条规则");
        setError(
          seenCompanies.get(rule.companyName),
          "companyName",
          "同一公司只能配置一条规则"
        );
      } else {
        seenCompanies.set(rule.companyName, rule._key);
      }
      if (!isPositiveInteger(rule.weight)) {
        setError(rule._key, "weight", "公司权重必须为正整数");
      }
      if (!rule.merchants.length) {
        setError(rule._key, "merchants", "请至少添加一个公司商户");
      } else if (
        rule.isEnabled &&
        !rule.merchants.some((merchant) => merchant.isEnabled)
      ) {
        setError(rule._key, "merchants", "启用的公司规则至少需要一个启用商户");
      }

      const allowedIds = companyMerchantIds(rule.companyName);
      const seenMerchants = new Map();
      rule.merchants.forEach((merchant) => {
        const target = merchantMap.value.get(merchant.merchantId);
        if (!target) {
          setError(merchant._key, "merchantId", "请选择有效商户");
        } else if (!allowedIds.has(merchant.merchantId)) {
          setError(merchant._key, "merchantId", "商户不属于当前公司主体");
        } else if (seenMerchants.has(merchant.merchantId)) {
          setError(merchant._key, "merchantId", "同一公司内不能重复选择商户");
          setError(
            seenMerchants.get(merchant.merchantId),
            "merchantId",
            "同一公司内不能重复选择商户"
          );
        } else {
          seenMerchants.set(merchant.merchantId, merchant._key);
        }
        if (!isPositiveInteger(merchant.weight)) {
          setError(merchant._key, "weight", "权重必须为正整数");
        }
        if (!merchant.bankCodes.length) {
          setError(merchant._key, "bankCodes", "请至少选择一个支持银行");
        } else {
          const invalidBank = merchant.bankCodes.find(
            (bankCode) =>
              !bankMap.value.has(bankCode) ||
              !bankSupportsMerchant(bankCode, merchant.merchantId)
          );
          if (invalidBank) {
            setError(merchant._key, "bankCodes", "存在与商户渠道不匹配的银行");
          }
        }
      });
    });
  }

  validationErrors.value = errors;
  if (Object.keys(errors).length) {
    message.warning(`配置中有 ${Object.keys(errors).length} 处需要修改`);
    nextTick(() => {
      document.querySelector(".ant-form-item-has-error")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
    return false;
  }
  return true;
};

const serializeBankRules = () =>
  bankRules.value.map((rule) => ({
    bankCode: rule.bankCode,
    bankName: bankMap.value.get(rule.bankCode)?.name || rule.bankName || "",
    isEnabled: rule.isEnabled,
    remark: String(rule.remark || "").trim(),
    merchants: rule.merchants.map((merchant) => ({
      merchantId: merchant.merchantId,
      weight: Number(merchant.weight),
      isEnabled: merchant.isEnabled,
      remark: String(merchant.remark || "").trim(),
    })),
  }));

const serializeCompanyRules = () =>
  companyRules.value.map((rule) => ({
    companyName: rule.companyName,
    weight: Number(rule.weight),
    isEnabled: rule.isEnabled,
    remark: String(rule.remark || "").trim(),
    merchants: rule.merchants.map((merchant) => ({
      merchantId: merchant.merchantId,
      weight: Number(merchant.weight),
      isEnabled: merchant.isEnabled,
      remark: String(merchant.remark || "").trim(),
      bankCodes: [...new Set(merchant.bankCodes)],
    })),
  }));

const buildPayload = (scope = "all") => {
  const targetScope = normalizeSaveScope(scope);
  const payload = { scope: targetScope };
  if (targetScope !== "company") {
    payload.bankRules = serializeBankRules();
  }
  if (targetScope !== "bank") {
    payload.companyRules = serializeCompanyRules();
  }
  return payload;
};

const loadConfig = async () => {
  loading.value = true;
  loadError.value = "";
  try {
    const [optionsJson, detailJson] = await Promise.all([
      get(analyticsUrl(`${API_PATH}/options`)),
      get(analyticsUrl(`${API_PATH}/detail`)),
    ]);
    if (optionsJson.code !== 200) {
      throw new Error(optionsJson.content || "可选商户和银行加载失败");
    }
    if (detailJson.code !== 200) {
      throw new Error(detailJson.content || "融合支付配置加载失败");
    }
    applyOptions(optionsJson.data || {});
    applyDetail(detailJson.data || {});
  } catch (error) {
    loadError.value = error?.message || "网络异常，请稍后重试";
  } finally {
    loading.value = false;
  }
};

const applySavedDetail = (data = {}, scope = "all") => {
  const targetScope = normalizeSaveScope(scope);
  if (targetScope === "all") {
    applyDetail(data);
    return;
  }
  if (targetScope === "bank" && Array.isArray(data.bankRules)) {
    bankRules.value = data.bankRules.map(makeBankRule);
  }
  if (targetScope === "company" && Array.isArray(data.companyRules)) {
    companyRules.value = data.companyRules.map(makeCompanyRule);
  }
  if (Object.prototype.hasOwnProperty.call(data, "statsUpdatedAt")) {
    statsUpdatedAt.value = data.statsUpdatedAt || "";
  }
  validationErrors.value = {};
};

const saveSuccessMessage = {
  all: "全部融合支付配置保存成功",
  bank: "银行规则保存成功",
  company: "公司规则保存成功",
};

const handleSave = async (scope = "all") => {
  const targetScope = normalizeSaveScope(scope);
  if (savingScope.value || !validateConfig(targetScope)) return;
  savingScope.value = targetScope;
  try {
    const json = await postJson(
      analyticsUrl(`${API_PATH}/update`),
      buildPayload(targetScope)
    );
    if (json.code === 200) {
      applySavedDetail(json.data || {}, targetScope);
      message.success(saveSuccessMessage[targetScope]);
    } else {
      message.error(json.content || "保存失败，请检查配置后重试");
    }
  } catch (error) {
    message.error(error?.content || error?.message || "保存失败，请稍后重试");
  } finally {
    savingScope.value = "";
  }
};

onMounted(loadConfig);
</script>

<style scoped>
.fusion-payment-page {
  padding: 24px;
}

.page-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;
}

.page-heading h1 {
  margin: 0 0 8px;
  color: rgba(0, 0, 0, 0.88);
  font-size: 24px;
  line-height: 1.35;
}

.page-heading p {
  margin: 0 0 4px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.6;
}

.rule-description,
.option-warning,
.config-section {
  margin-bottom: 16px;
}

.rule-description ol {
  margin: 4px 0 0;
  padding-left: 20px;
  line-height: 1.8;
}

.load-result {
  border-radius: 8px;
  background: #ffffff;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.primary-action-button.ant-btn {
  color: #ffffff;
  background: #123c6b;
  border-color: #123c6b;
  box-shadow: 0 2px 6px rgba(18, 60, 107, 0.24);
  font-weight: 600;
}

.primary-action-button.ant-btn:not(:disabled):hover,
.primary-action-button.ant-btn:not(:disabled):focus {
  color: #ffffff;
  background: #0b2d52;
  border-color: #0b2d52;
}

.primary-action-button.ant-btn:focus-visible {
  outline: 3px solid rgba(18, 60, 107, 0.3);
  outline-offset: 2px;
}

.add-rule-button.ant-btn {
  color: #0958d9;
  background: #ffffff;
  border-color: #69b1ff;
  box-shadow: none;
  font-weight: 600;
}

.add-rule-button.ant-btn:not(:disabled):hover,
.add-rule-button.ant-btn:not(:disabled):focus {
  color: #003eb3;
  background: #e6f4ff;
  border-color: #1677ff;
}

.add-rule-button.ant-btn:focus-visible {
  outline: 2px solid rgba(22, 119, 255, 0.35);
  outline-offset: 2px;
}

.section-actions {
  display: flex;
  justify-content: flex-end;
}

.bottom-save-area {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0 8px;
  border-top: 1px solid #e8edf3;
}

.bottom-save-button {
  min-width: 220px;
  min-height: 44px;
}

.rule-list,
.merchant-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rule-card {
  border-color: #d9e7ff;
  border-radius: 8px;
}

.company-rule-card {
  border-color: #ffe2bf;
}

.rule-card.is-disabled {
  background: #fafafa;
}

.merchant-list-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 4px 0 12px;
}

.merchant-list-heading > div {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.merchant-editor {
  padding: 12px 16px 0;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.merchant-editor:hover {
  border-color: #d9d9d9;
}

.merchant-editor.is-disabled {
  background: #f5f5f5;
}

.merchant-editor-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.amount-text {
  font-variant-numeric: tabular-nums;
}

.inline-error {
  margin-bottom: 12px;
}

.nested-empty {
  margin: 8px 0;
  padding: 16px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .fusion-payment-page {
    padding: 8px 0;
  }

  .page-heading {
    flex-direction: column;
    gap: 16px;
  }

  .page-heading > :last-child,
  .page-heading > :last-child :deep(.ant-space-item) {
    width: 100%;
  }

  .page-heading > :last-child :deep(.ant-btn) {
    width: 100%;
    min-height: 44px;
  }

  .fusion-payment-page :deep(.ant-btn) {
    min-height: 44px;
  }

  .config-section :deep(.ant-card-head-wrapper),
  .rule-card :deep(.ant-card-head-wrapper) {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .config-section :deep(.ant-card-extra),
  .rule-card :deep(.ant-card-extra) {
    width: 100%;
    margin-left: 0;
  }

  .section-actions {
    width: 100%;
  }

  .section-actions :deep(.ant-space-item) {
    flex: 1 1 160px;
  }

  .section-actions :deep(.ant-btn),
  .bottom-save-button {
    width: 100%;
  }

  .bottom-save-area {
    padding-top: 16px;
  }

  .merchant-list-heading,
  .merchant-editor-heading {
    align-items: flex-start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .merchant-editor {
    transition: none;
  }
}
</style>
